<template>
  <div class="row justify-start item-center q-col-gutter-md">
    <model-form-input
      field="config.siteSelectorTitle"
      value-type="String"
      class="col-6"
      label="Title"
    />
    <model-form-toggle
      field="config.siteSelectorShowHeading"
      value-type="Boolean"
      class="col-3"
      label="Show Heading"
      size="lg"
    />
    <div class="col-3">
      <q-btn
        label="Set Site Order"
        size="md"
        outline
        @click="$refs.siteDisplayOrderDialog.show()"
      />
    </div>
    <model-form-select
      field="config.siteSelectorInitialDisplayType"
      value-type="String"
      class="col-3"
      label="Selector Initial Display Preference"
      :options="$mh.asOptions(SELECTOR_DISPLAY_TYPES.ALL)"
      map-options
      emit-value
    />
    <model-form-checkbox
      field="config.siteSelectorDefaultExpanded"
      class="col-3"
      value-type="Boolean"
      label="Default expanded locations expansion"
      bottom-slots
    />
    <model-form-checkbox
      field="config.siteSelectorCompactView"
      class="col-3"
      value-type="Boolean"
      label="Compact current location button"
      bottom-slots
    />

    <form-control-style-editor
      v-if="section.config.siteSelectorInitialDisplayType !== SELECTOR_DISPLAY_TYPES.SELECT_BOX"
      :field-type="section.config.siteSelectorFieldType"
      :field-outlined="section.config.siteSelectorFieldOutlined"
      class="row  col-6"
      @update:model-value="setSelectorFieldStyle"
    />

    <button-style-editor
      class="row col-12 q-col-gutter-md q-mb-md items-start"
      :button-type-label="section.config.siteSelectorInitialDisplayType === SELECTOR_DISPLAY_TYPES.SELECT_BOX ? 'Select Type' : 'Location Button Type'"
      :color-label="section.config.siteSelectorInitialDisplayType === SELECTOR_DISPLAY_TYPES.SELECT_BOX ? 'Select Color' : 'Location Button Color'"
      :border-radius-label="section.config.siteSelectorInitialDisplayType === SELECTOR_DISPLAY_TYPES.SELECT_BOX ? 'Select Border Radius' : 'Location Border Radius'"
      :button-type="section.config.siteSelectorLocationButtonType"
      :color-name="section.config.siteSelectorLocationButtonColor"
      :custom-color-hex="section.config.siteSelectorLocationButtonColorHex"
      :button-border-radius="section.config.siteSelectorLocationButtonBorderRadius"
      @update:model-value="setButtonStyle"
    />

    <template v-if="section.config.siteSelectorInitialDisplayType !== SELECTOR_DISPLAY_TYPES.SELECT_BOX">
      <model-form-checkbox
        field="config.siteSelectorLocationShowSiteDescription"
        class="col-3"
        value-type="Boolean"
        label="Show Site Description"
        bottom-slots
      />
      <model-form-checkbox
        field="config.siteSelectorLocationShowSiteImage"
        class="col-3"
        value-type="Boolean"
        label="Show Site Image"
        bottom-slots
      >
        <q-tooltip>
          Please make sure you upload site thumbnail images in the site settings.
        </q-tooltip>
      </model-form-checkbox>

      <template v-if="!!section.config.siteSelectorLocationShowSiteImage">
        <color-selection
          v-if="section.config.siteSelectorLocationShowSiteImage"
          class="col-6"
          :color-name="section.config.siteSelectorCardColor"
          :custom-color-hex="section.config.siteSelectorCardColorHex"
          label="Location Card Background Color"
          @update:model-value="setCardColor"
        />
      </template>
      <div
        class="col-3 q-pr-sm q-pb-sm"
      >
        <q-item-label>Card Border Radius</q-item-label>
        <q-slider
          v-model="section.config.siteSelectorCardBorderRadius"
          label
          label-always
          switch-label-side
          type="number"
          :min="0"
          :max="32"
          :step="4"
        />
      </div>
    </template>
    <model-form-checkbox
      field="config.siteSelectorRedirectToHomePage"
      class="col-3"
      value-type="Boolean"
      label="Redirected to Home Page"
      bottom-slots
    >
      <q-tooltip>
        When this option is enabled, the user will be redirected to the home page rather than staying on the current URL path during a redirect to a new site
      </q-tooltip>
    </model-form-checkbox>
    <preview
      :section="section"
      class="q-mb-xl"
    />
    <set-display-order-dialog
      ref="siteDisplayOrderDialog"
      title="Set Site Display Order"
      :list="siteSelectorSites"
      :order-options="DISPLAY_ORDER_OPTIONS.ALL"
      @reorder="handleSiteReorder"
    />
  </div>
</template>
<script>
import Model from 'src/models/model'
import Section, { SELECTOR_DISPLAY_TYPES, TEXT_FIELD_TYPES } from 'src/models/page/Section'
import { DISPLAY_ORDER_OPTIONS } from 'src/models/site/Site'
import ButtonStyleEditor from 'src/modules/admin/components/ButtonStyleEditor'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import FormControlStyleEditor from 'src/modules/admin/components/FormControlStyleEditor'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import Preview from 'src/modules/consumer/components/Preview'
export default {
  name: 'SiteSelectorEditor',
  components: {
    Preview,
    ButtonStyleEditor,
    SetDisplayOrderDialog,
    FormControlStyleEditor,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  configuration: ['currentCircuit', 'sites'],
  data () {
    return {
      addNewOptionError: null,
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  computed: {
    siteSelectorSites () {
      return _.filter(this.sites.data, site => !site.isLibrary && !site.consumerConfig.hideOnLocationSelectPage)
    }
  },
  created () {
    _.extend(this, {
      DISPLAY_ORDER_OPTIONS,
      SELECTOR_DISPLAY_TYPES,
      TEXT_FIELD_TYPES
    })
  },
  methods: {
    setButtonStyle (buttonType, colorName, customColorHex, buttonRadius) {
      this.section.config.siteSelectorLocationButtonType = buttonType
      this.section.config.siteSelectorLocationButtonColor = colorName
      this.section.config.siteSelectorLocationButtonColorHex = customColorHex
      this.section.config.siteSelectorLocationButtonBorderRadius = buttonRadius
    },
    setSelectorFieldStyle (fieldType, fieldOutlined) {
      this.section.config.siteSelectorFieldType = fieldType
      this.section.config.siteSelectorFieldOutlined = fieldOutlined
    },
    setCardColor (colorName, customColorHex) {
      this.section.config.siteSelectorCardColor = colorName
      this.section.config.siteSelectorCardColorHex = customColorHex
    },
    async handleSiteReorder ({ reorderedList }) {
      this.sites.data = reorderedList
      this.sites.validateDisplayOrder()

      await Model.configuration.batchSave(async () => {
        const saveMutations = _(this.sites.data).filter({ isDirty: true }).map(model => model.save()).value()
        await Promise.all(saveMutations)
      })
    }
  }
}

</script>
